import type {
  ChonKenhKhiXoaHopDong,
  DongXemTruocXoa,
  KenhGanHopDong,
  PartnerContract,
  TinhHinhHopDong,
  XemTruocXoaHopDong,
} from '@qlk/shared';
import { useEffect, useMemo, useState } from 'react';

import { HopThoai, HopXacNhan, Nut, O, OChonTruong, ngayDay } from '../../../ui/index.js';
import { useXemTruocXoaHopDong, useXoaHopDong } from '../api.js';

/**
 * Hộp thoại xoá (mềm) một hợp đồng Network.
 *
 * Hợp đồng còn kênh gắn vẫn xoá được, nhưng **mỗi kênh** phải chọn đi đâu:
 * chuyển sang hợp đồng khác của cùng đối tác, hoặc giữ link không hợp đồng và
 * dùng "Tỷ lệ riêng" của link. Thiếu một kênh là không cho xoá — để link trỏ
 * vào hợp đồng đã xoá sẽ làm tỷ lệ kênh đó âm thầm về 0.
 *
 * Nút xoá chỉ bật khi: đã chọn đủ kênh, đã nhập lý do, và gõ đúng số hợp đồng.
 */

type Cach = 'chuyen-hop-dong' | 'ty-le-rieng';

interface LuaChon {
  cach: Cach;
  contractIdMoi: string;
  /** Chuỗi để ô nhập giữ nguyên những gì người dùng gõ. */
  tyLe: string;
}

/** Lựa chọn mặc định của một kênh: chuyển sang hợp đồng khác nếu có. */
function macDinh(kenh: KenhGanHopDong, hopDongChuyenDuoc: PartnerContract[]): LuaChon {
  const dau = hopDongChuyenDuoc[0];
  return {
    cach: dau ? 'chuyen-hop-dong' : 'ty-le-rieng',
    contractIdMoi: dau?.id ?? '',
    tyLe: kenh.sharePercent ?? '',
  };
}

/**
 * Một lựa chọn đã đủ dữ liệu chưa. Tách khỏi JSX để test được mà không dựng
 * DOM.
 */
export function luaChonDaDu(x: { cach: Cach; contractIdMoi: string; tyLe: string }): boolean {
  if (x.cach === 'chuyen-hop-dong') return x.contractIdMoi !== '';
  const so = Number(x.tyLe);
  return x.tyLe.trim() !== '' && !Number.isNaN(so) && so >= 0 && so <= 100;
}

export function HopThoaiXoaHopDong({
  hopDong,
  tinhHinh,
  onDong,
}: {
  hopDong: PartnerContract;
  tinhHinh: TinhHinhHopDong;
  onDong: () => void;
}) {
  const xoa = useXoaHopDong(hopDong.id);
  const xemTruoc = useXemTruocXoaHopDong(hopDong.id);

  const [lyDo, datLyDo] = useState('');
  const [chon, datChon] = useState<Record<string, LuaChon>>(() =>
    Object.fromEntries(
      tinhHinh.kenhCanChon.map((k) => [k.linkId, macDinh(k, tinhHinh.hopDongChuyenDuoc)]),
    ),
  );

  const dayDu = tinhHinh.kenhCanChon.every((k) => {
    const c = chon[k.linkId];
    return c !== undefined && luaChonDaDu(c);
  });

  const than: ChonKenhKhiXoaHopDong[] = useMemo(
    () =>
      tinhHinh.kenhCanChon.map((k) => {
        /* `dayDu` đã chặn thiếu, nhánh này chỉ để kiểu không phải nullable. */
        const c = chon[k.linkId] ?? macDinh(k, tinhHinh.hopDongChuyenDuoc);
        return c.cach === 'chuyen-hop-dong'
          ? { linkId: k.linkId, cach: c.cach, contractIdMoi: c.contractIdMoi }
          : { linkId: k.linkId, cach: c.cach, sharePercent: Number(c.tyLe) };
      }),
    [chon, tinhHinh.kenhCanChon, tinhHinh.hopDongChuyenDuoc],
  );

  /*
   * Xem trước tính lại mỗi lần đổi lựa chọn: người dùng phải thấy "tỷ lệ kỳ
   * 07–09 đổi từ bao nhiêu sang bao nhiêu" **trước** khi bấm xoá.
   */
  const khoaThan = JSON.stringify(than);
  const goiXemTruoc = xemTruoc.mutate;

  useEffect(() => {
    if (!dayDu) return;
    goiXemTruoc({ kenh: JSON.parse(khoaThan) as ChonKenhKhiXoaHopDong[] });
  }, [khoaThan, dayDu, goiXemTruoc]);

  return (
    <HopXacNhan
      title={`Xoá hợp đồng ${hopDong.contractNo}?`}
      nhanXacNhan="Xoá hợp đồng"
      nguyHiem
      rong="xl"
      chan={!dayDu || lyDo.trim().length < 3}
      goLai={{ nhan: 'Gõ lại số hợp đồng để xác nhận', giaTri: hopDong.contractNo }}
      dangChay={xoa.isPending}
      loi={xoa.error?.message ?? null}
      onDong={onDong}
      onXacNhan={() => {
        xoa.mutate({ lyDo: lyDo.trim(), kenh: than }, { onSuccess: onDong });
      }}
    >
      <div className="space-y-4">
        <p>
          Hợp đồng được <strong>xoá mềm</strong>: biến khỏi danh sách và khỏi mọi phép tính, nhưng
          còn khôi phục được trong 30 ngày ở mục "Hợp đồng đã xoá". Không dòng nào bị xoá hẳn.
        </p>

        {tinhHinh.kenhCanChon.length > 0 && (
          <section>
            <h3 className="mb-2 text-co-ban font-semibold text-chu">
              {tinhHinh.kenhCanChon.length} kênh đang gắn hợp đồng này
            </h3>
            <p className="mb-3 text-phu text-chu-mo">
              Chọn cho từng kênh, nếu không tỷ lệ Network của kênh đó sẽ về 0.
            </p>

            <ul className="space-y-3">
              {tinhHinh.kenhCanChon.map((k) => {
                const c = chon[k.linkId] ?? macDinh(k, tinhHinh.hopDongChuyenDuoc);
                const doi = (moi: Partial<LuaChon>) => {
                  datChon((truoc) => ({ ...truoc, [k.linkId]: { ...c, ...moi } }));
                };

                return (
                  <li
                    key={k.linkId}
                    className="grid grid-cols-1 items-end gap-3 rounded-[12px] border border-vien p-3 md:grid-cols-[minmax(130px,1fr)_minmax(150px,1fr)_minmax(140px,1fr)]"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-co-ban font-medium text-chu">{k.channelName}</p>
                      <p className="text-phu text-chu-mo">
                        Vào {ngayDay(k.joinedAt)}
                        {k.leftAt === null ? '' : ` → rời ${ngayDay(k.leftAt)}`} ·{' '}
                        {k.tyLeHienTai.replace('.', ',')}%
                      </p>
                    </div>

                    <OChonTruong
                      label="Cách xử lý"
                      value={c.cach}
                      onChange={(v) => {
                        doi({ cach: v as Cach });
                      }}
                      options={[
                        ...(tinhHinh.hopDongChuyenDuoc.length > 0
                          ? [{ value: 'chuyen-hop-dong', label: 'Chuyển sang hợp đồng khác' }]
                          : []),
                        { value: 'ty-le-rieng', label: 'Giữ link, dùng tỷ lệ riêng' },
                      ]}
                    />

                    {c.cach === 'chuyen-hop-dong' ? (
                      <OChonTruong
                        label="Hợp đồng mới"
                        value={c.contractIdMoi}
                        onChange={(v) => {
                          doi({ contractIdMoi: v });
                        }}
                        options={tinhHinh.hopDongChuyenDuoc.map((h) => ({
                          value: h.id,
                          label: `${h.contractNo} · ${h.defaultSharePercent.replace('.', ',')}%`,
                        }))}
                      />
                    ) : (
                      <O
                        label="Network giữ (%)"
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={c.tyLe}
                        onChange={(v) => {
                          doi({ tyLe: v });
                        }}
                        required
                        {...(k.sharePercent === null
                          ? { moTa: 'Link chưa có tỷ lệ riêng — phải nhập.' }
                          : {})}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {tinhHinh.kenhCanChon.length > 0 && (
          <XemTruoc
            dangChay={xemTruoc.isPending}
            loi={xemTruoc.error?.message ?? null}
            ketQua={xemTruoc.data}
            dayDu={dayDu}
          />
        )}

        <O
          label="Lý do xoá"
          value={lyDo}
          onChange={datLyDo}
          required
          nhieuDong
          moTa="Bắt buộc, ghi vào nhật ký thay đổi của hợp đồng."
        />

        {!dayDu && (
          <p className="text-phu text-warn-chu">
            Còn kênh chưa chọn xong cách xử lý — chưa xoá được.
          </p>
        )}
      </div>
    </HopXacNhan>
  );
}

/** Bảng "trước → sau" của tỷ lệ Network theo kỳ. */
function XemTruoc({
  dangChay,
  loi,
  ketQua,
  dayDu,
}: {
  dangChay: boolean;
  loi: string | null;
  ketQua: XemTruocXoaHopDong | undefined;
  dayDu: boolean;
}) {
  if (!dayDu) return null;

  if (loi !== null) {
    return (
      <p role="alert" className="text-phu text-bad-chu">
        Không xem trước được: {loi}
      </p>
    );
  }

  if (dangChay || !ketQua) {
    return <p className="text-phu text-chu-mo">Đang tính tỷ lệ sau khi xoá…</p>;
  }

  const kenh = [...new Map(ketQua.dong.map((d) => [d.channelId, d.channelName])).entries()];
  const chuaVaoNetwork = ketQua.dong.some((d) => !d.coNetworkTruoc);

  return (
    <section>
      <h3 className="mb-2 text-co-ban font-semibold text-chu">
        Tỷ lệ Network sau khi xoá, các kỳ gần nhất
      </h3>

      {/* Bảng rộng thì cuộn ngang trong khung riêng, không đẩy cả hộp thoại. */}
      <div className="overflow-x-auto rounded-[10px] border border-vien">
        <table className="w-full min-w-[460px] border-collapse text-phu">
          <caption className="sr-only">Tỷ lệ Network trước và sau khi xoá hợp đồng</caption>
          <thead>
            <tr className="bg-header-bang">
              <th scope="col" className="px-2 py-2 text-left font-medium text-chu-phu">
                Kênh
              </th>
              {ketQua.ky.map((k) => (
                <th key={k} scope="col" className="px-2 py-2 text-right font-medium text-chu-phu">
                  {k}
                  {k === ketQua.kyDangDienRa && (
                    <span className="block text-nhom-menu font-normal text-chu-mo">còn chạy</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kenh.map(([channelId, channelName]) => (
              <tr key={channelId} className="border-t border-vien">
                <th scope="row" className="px-2 py-2 text-left font-normal text-chu">
                  {channelName}
                </th>
                {ketQua.ky.map((k) => (
                  <OTyLeKy
                    key={k}
                    dong={ketQua.dong.find((x) => x.channelId === channelId && x.period === k)}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {chuaVaoNetwork && (
        <p className="mt-2 text-phu text-chu-mo">
          "chưa vào Network" là kỳ không có ngày nào thuộc Network: ngày vào Network của kênh muộn
          hơn kỳ đó, nên xoá hợp đồng không đổi gì ở kỳ đó.
        </p>
      )}

      {/* Chỉ cảnh báo khi việc xoá **làm đổi** số của một kỳ còn dở dang. */}
      {ketQua.dong.some((d) => d.thieuNetworkSau && d.coNetworkSau && d.truoc !== d.sau) && (
        <p className="mt-2 text-phu text-warn-chu">
          Có kỳ chỉ một phần số ngày thuộc Network và tỷ lệ đổi sau khi xoá — báo cáo kỳ đó sẽ kèm
          dải cảnh báo.
        </p>
      )}
    </section>
  );
}

/** Nội dung một ô "trước → sau" của bảng xem trước. */
export type NoiDungOTyLe =
  | { kieu: 'khong-co' }
  | { kieu: 'chua-vao-network' }
  | { kieu: 'ty-le'; truoc: string; sau: string; doi: boolean };

/**
 * Ô "trước → sau" hiển thị gì.
 *
 * Kỳ **không có ngày nào** thuộc Network phải ghi "chưa vào Network" chứ không
 * ghi "0,00%": `tyLe` của kỳ đó cũng là 0 nên in thẳng con số sẽ đọc như một tỷ
 * lệ đã tính. Lỗi thấy với RouteNote01 — 4 kênh đều vào Network ngày
 * 08–09/10/2026, nên cả ba kỳ 07–09/2026 hiện "0,00% → 0,00%".
 *
 * Tách thành hàm thuần để test được mà không dựng DOM.
 */
export function noiDungOTyLe(dong: DongXemTruocXoa | undefined): NoiDungOTyLe {
  if (!dong) return { kieu: 'khong-co' };
  if (!dong.coNetworkTruoc && !dong.coNetworkSau) return { kieu: 'chua-vao-network' };

  return {
    kieu: 'ty-le',
    truoc: dong.coNetworkTruoc ? `${dong.truoc.replace('.', ',')}%` : '—',
    sau: dong.coNetworkSau ? `${dong.sau.replace('.', ',')}%` : '—',
    doi: dong.truoc !== dong.sau,
  };
}

function OTyLeKy({ dong }: { dong: DongXemTruocXoa | undefined }) {
  const o = noiDungOTyLe(dong);

  if (o.kieu === 'khong-co') {
    return <td className="px-2 py-2 text-right text-chu-mo">—</td>;
  }

  if (o.kieu === 'chua-vao-network') {
    return (
      <td className="px-2 py-2 text-right text-tieu-de-cot whitespace-nowrap text-chu-mo">
        chưa vào Network
      </td>
    );
  }

  return (
    <td className="so-lieu px-2 py-2 text-right whitespace-nowrap">
      <span className="text-chu-mo">{o.truoc}</span>
      <span aria-hidden="true"> → </span>
      <span className={o.doi ? 'font-semibold text-warn-chu' : 'text-chu'}>{o.sau}</span>
    </td>
  );
}

/** Vì sao không xoá được, kèm kỳ và sao kê liên quan. */
export function LyDoKhongXoaDuoc({
  tinhHinh,
  onDong,
  onKetThuc,
  dangKetThuc,
}: {
  tinhHinh: TinhHinhHopDong;
  onDong: () => void;
  onKetThuc: () => void;
  dangKetThuc: boolean;
}) {
  return (
    <HopThoai
      title="Không xoá được hợp đồng này"
      rong="md"
      onDong={onDong}
      chanHop={
        <>
          <Nut kieu="phu" onClick={onDong}>
            Đóng
          </Nut>
          <Nut kieu="chinh" dangChay={dangKetThuc} onClick={onKetThuc}>
            Kết thúc hợp đồng
          </Nut>
        </>
      }
    >
      <p className="mb-3 text-co-ban text-chu-phu">
        Hợp đồng đã được dùng để chia tiền, nên xoá đi sẽ làm số của kỳ cũ đổi theo.
      </p>

      {tinhHinh.kyLienQuan.length > 0 && (
        <section className="mb-3">
          <h3 className="mb-1 text-phu font-semibold text-chu-phu">Kỳ đã chốt</h3>
          <ul className="space-y-1 text-phu text-chu">
            {tinhHinh.kyLienQuan.map((k) => (
              <li key={`${k.channelId}-${k.period}`}>
                {k.period} · {k.channelName} · {k.status}
              </li>
            ))}
          </ul>
        </section>
      )}

      {tinhHinh.saoKeLienQuan.length > 0 && (
        <section className="mb-3">
          <h3 className="mb-1 text-phu font-semibold text-chu-phu">
            Sao kê không còn chờ đối soát
          </h3>
          <ul className="space-y-1 text-phu text-chu">
            {tinhHinh.saoKeLienQuan.map((s) => (
              <li key={s.id}>
                {s.period} · {s.status}
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-phu text-chu-mo">
        Cách làm: "Kết thúc hợp đồng" để đặt ngày kết thúc (lịch sử chia tiền vẫn giữ), hoặc mở lại
        kỳ đã chốt rồi xoá.
      </p>
    </HopThoai>
  );
}
